import React from 'react';
import { HotelStaffMember } from '../../types/admin';

interface StaffListProps {
  staff: HotelStaffMember[];
  onEditStaff?: (staff: HotelStaffMember) => void;
}

// Role configuration supporting both backend (snake_case) and frontend formats
const ROLE_CONFIG: Record<string, { label: string; color: string }> = {
  // Backend format (snake_case)
  hotel_admin: { label: 'Administrateur', color: 'bg-purple-100 text-purple-800' },
  hotel_manager: { label: 'Manager', color: 'bg-blue-100 text-blue-800' },
  hotel_supervisor: { label: 'Superviseur', color: 'bg-indigo-100 text-indigo-800' },
  hotel_accountant: { label: 'Comptable', color: 'bg-amber-100 text-amber-800' },
  receptionist: { label: 'Réceptionniste', color: 'bg-green-100 text-green-800' },
  staff: { label: 'Personnel', color: 'bg-gray-100 text-gray-800' },
  // DB format (uppercase) and PascalCase for backwards compatibility
  ADMIN: { label: 'Administrateur', color: 'bg-purple-100 text-purple-800' },
  MANAGER: { label: 'Manager', color: 'bg-blue-100 text-blue-800' },
  SUPERVISOR: { label: 'Superviseur', color: 'bg-indigo-100 text-indigo-800' },
  ACCOUNTANT: { label: 'Comptable', color: 'bg-amber-100 text-amber-800' },
  RECEPTIONIST: { label: 'Réceptionniste', color: 'bg-green-100 text-green-800' },
  Administrator: { label: 'Administrateur', color: 'bg-purple-100 text-purple-800' },
  Manager: { label: 'Manager', color: 'bg-blue-100 text-blue-800' },
  Supervisor: { label: 'Superviseur', color: 'bg-indigo-100 text-indigo-800' },
  Accountant: { label: 'Comptable', color: 'bg-amber-100 text-amber-800' },
  Receptionist: { label: 'Réceptionniste', color: 'bg-green-100 text-green-800' },
};

export function StaffList({ staff, onEditStaff }: StaffListProps) {
  const formatLastLogin = (timestamp?: string) => {
    if (!timestamp) return 'Jamais';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffHours < 1) return 'Il y a moins d\'1h';
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    return `Il y a ${diffDays}j`;
  };

  const getRoleBadge = (role: string) => {
    const config = ROLE_CONFIG[role] || { label: role, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (status: HotelStaffMember['status']) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      active: 'Actif',
      inactive: 'Inactif'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Personnel de l'Hôtel</h3>
        <p className="text-sm text-gray-600 mt-1">{staff.length} membre(s) du personnel</p>
      </div>
      <div className="divide-y divide-gray-200">
        {staff.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-500">
            Aucun membre du personnel
          </div>
        ) : (
          staff.map((member) => (
            <div 
              key={member.id} 
              className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => onEditStaff?.(member)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <span className="text-gray-600 font-medium">
                      {member.fullName?.split(' ').map(n => n[0]).join('') || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <p className="font-medium text-gray-900">{member.fullName}</p>
                      {getRoleBadge(member.role)}
                      {getStatusBadge(member.status)}
                    </div>
                    <p className="text-sm text-gray-600">{member.email}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Dernière connexion: {formatLastLogin(member.lastLogin)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">{member.phone}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
